import React, { useState, useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { jwtDecode } from 'jwt-decode';

// Componente
import AuthPage from './components/auth/AuthPage';
import ChatPage from './components/chat/ChatPage';

// Utilități și API
import { 
  API_BASE_URL,
  checkBackendStatus, 
  getConversations, 
  getConversation, 
  createConversation, 
  deleteConversation as apiDeleteConversation, 
  getModels, 
  getCurrentUser
} from './utils/api';

// Componente pentru autentificare

// Componenta pentru formularul de Login
function LoginForm({ onLogin, switchToRegister }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // FormData este necesar pentru a respecta formatul OAuth2
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);

      const response = await fetch(`${API_BASE_URL}/token`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Autentificare eșuată');
      }

      const data = await response.json();
      // Trimitem token-ul înapoi la App.js
      onLogin(data.access_token);
    } catch (error) {
      setError(error.message || 'A apărut o eroare la autentificare');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md p-8 space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
          Autentificare
        </h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nume utilizator
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Parolă
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Se încarcă...' : 'Autentificare'}
          </button>
        </form>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Nu ai cont?{' '}
            <button
              onClick={switchToRegister}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Înregistrează-te
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

// Componenta pentru formularul de Register
function RegisterForm({ onRegister, switchToLogin }) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          email,
          password,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Înregistrare eșuată');
      }

      // După înregistrare, autentificăm automat utilizatorul
      onRegister();
      switchToLogin(); // Redirecționăm către pagina de login
    } catch (error) {
      setError(error.message || 'A apărut o eroare la înregistrare');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md p-8 space-y-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white">
          Înregistrare
        </h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Nume utilizator
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Parolă
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {isLoading ? 'Se încarcă...' : 'Înregistrare'}
          </button>
        </form>
        
        <div className="text-center mt-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Ai deja cont?{' '}
            <button
              onClick={switchToLogin}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Autentifică-te
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

// Componenta pentru blocuri de cod în Markdown
const CodeBlock = ({ language, children, showLineNumbers = true }) => {
  const [copied, setCopied] = useState(false);
  const code = String(children).replace(/\n$/, '');

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center px-4 py-2 bg-gray-100 dark:bg-gray-800">
        <span className="text-sm text-gray-500 dark:text-gray-400">{language || 'text'}</span>
        <button 
          onClick={copyToClipboard}
          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          {copied ? 'Copiat!' : 'Copiază'}
        </button>
      </div>
      <SyntaxHighlighter 
        language={language || 'text'} 
        style={vscDarkPlus}
        showLineNumbers={showLineNumbers}
        wrapLines={true}
        customStyle={{
          margin: 0,
          padding: '1rem',
          fontSize: '0.9rem',
          backgroundColor: '#1E1E1E',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

// Componenta pentru formatarea Markdown
function MarkdownRenderer({ content }) {
  if (!content) return null;

  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <CodeBlock
                language={match[1]}
                showLineNumbers={true}
                {...props}
              >
                {children}
              </CodeBlock>
            ) : (
              <code className="bg-gray-100 dark:bg-gray-800 text-pink-600 dark:text-pink-400 px-1 py-0.5 rounded font-mono text-sm" {...props}>
                {children}
              </code>
            );
          },
          // Stiluri pentru diferite elemente Markdown
          h1: ({ node, ...props }) => <h1 className="text-2xl font-bold mt-6 mb-4" {...props} />,
          h2: ({ node, ...props }) => <h2 className="text-xl font-bold mt-5 mb-3" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-lg font-bold mt-4 mb-2" {...props} />,
          p: ({ node, ...props }) => <p className="mb-4" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-6 mb-4" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-6 mb-4" {...props} />,
          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
          a: ({ node, ...props }) => (
            <a 
              className="text-blue-600 hover:underline" 
              target="_blank" 
              rel="noopener noreferrer" 
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote 
              className="border-l-4 border-gray-300 dark:border-gray-700 pl-4 italic text-gray-700 dark:text-gray-300 my-4" 
              {...props}
            />
          ),
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border border-gray-300 dark:border-gray-700" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-gray-100 dark:bg-gray-800" {...props} />,
          th: ({ node, ...props }) => (
            <th 
              className="py-2 px-4 border-b border-gray-300 dark:border-gray-700 text-left font-semibold" 
              {...props} 
            />
          ),
          td: ({ node, ...props }) => (
            <td 
              className="py-2 px-4 border-b border-gray-300 dark:border-gray-700" 
              {...props} 
            />
          ),
          hr: ({ node, ...props }) => <hr className="my-6 border-t border-gray-300 dark:border-gray-700" {...props} />,
          img: ({ node, ...props }) => (
            <img 
              className="max-w-full h-auto my-4 rounded-md" 
              {...props} 
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

function App() {
  // State pentru interfața de chat
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [backendError, setBackendError] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [showModelSelector, setShowModelSelector] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false); // Stare pentru modul privat
  const [privateConversationsCache, setPrivateConversationsCache] = useState(
    JSON.parse(localStorage.getItem('privateConversationsCache') || '{}')
  ); // Cache pentru conversații private
  const messagesEndRef = useRef(null);
  const [activeEventSource, setActiveEventSource] = useState(null);
  const [backendAvailable, setBackendAvailable] = useState(true);
  const [checkingBackend, setCheckingBackend] = useState(true);
  
  // State pentru autentificare
  const [token, setToken] = useState(localStorage.getItem('token') || '');
  const [user, setUser] = useState(null);
  const [authView, setAuthView] = useState('login'); // 'login' sau 'register'

  // Verificăm starea backend-ului la încărcarea aplicației
  useEffect(() => {
    const checkBackend = async () => {
      setCheckingBackend(true);
      try {
        const isAvailable = await checkBackendStatus();
        setBackendAvailable(isAvailable);
        
        if (!isAvailable) {
          setBackendError(`Nu se poate conecta la backend. Asigurați-vă că serverul FastAPI rulează.`);
        } else {
          setBackendError(null);
        }
      } catch (error) {
        console.error('Eroare la verificarea backend-ului:', error);
        setBackendAvailable(false);
        setBackendError(`Nu se poate conecta la backend. Asigurați-vă că serverul FastAPI rulează.`);
      } finally {
        setCheckingBackend(false);
      }
    };
    
    checkBackend();
  }, []);

  // Verificăm dacă token-ul există și este valid
  useEffect(() => {
    if (token && backendAvailable) {
      try {
        // Decodăm token-ul pentru a obține informații despre utilizator
        const decoded = jwtDecode(token);
        
        // Verificăm dacă token-ul a expirat
        const currentTime = Date.now() / 1000;
        if (decoded.exp && decoded.exp < currentTime) {
          // Token-ul a expirat, delogăm utilizatorul
          handleLogout();
        } else {
          // Token-ul este valid, setăm utilizatorul și încărcăm datele
          setUser({ username: decoded.sub });
          localStorage.setItem('token', token);
          
          // Încărcăm datele utilizatorului doar dacă backend-ul este disponibil
          fetchUserData();
        }
      } catch (error) {
        console.error('Eroare la decodarea token-ului:', error);
        handleLogout();
      }
    }
  }, [token, backendAvailable]);

  // Încărcă conversațiile și modelele dacă utilizatorul este autentificat și backend-ul este disponibil
  useEffect(() => {
    if (user && backendAvailable) {
      fetchConversations();
      fetchModels();
    }
  }, [user, backendAvailable]);

  // Auto-scroll la mesaje noi
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Preia informațiile utilizatorului curent
  const fetchUserData = async () => {
    try {
      const userData = await getCurrentUser(token);
      setUser(userData);
    } catch (error) {
      console.error('Eroare la preluarea datelor utilizatorului:', error);
      // Dacă avem o eroare 401, delogăm utilizatorul
      if (error.message && error.message.includes('401')) {
        handleLogout();
      }
    }
  };

  // Preia lista modelelor disponibile
  const fetchModels = async () => {
    try {
      const data = await getModels(token);
      
      // Verificăm că avem modele disponibile
      if (Array.isArray(data) && data.length > 0) {
        setAvailableModels(data);
        // Nu setăm niciun model implicit aici, lăsăm ca modelul să fie setat când
        // se încarcă sau se creează o conversație
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setAvailableModels([]);
    }
  };

  // Hook pentru a salva cache-ul de conversații private în localStorage
  useEffect(() => {
    localStorage.setItem('privateConversationsCache', JSON.stringify(privateConversationsCache));
  }, [privateConversationsCache]);

  // Preia conversațiile existente
  const fetchConversations = async () => {
    try {
      if (!token) return;
      
      // Folosim starea salvată a backend-ului în loc să verificăm din nou
      if (!backendAvailable) {
        return;
      }
      
      console.log("Începe preluarea conversațiilor de la backend...");
      const data = await getConversations(token);
      console.log("Conversații primite de la backend:", data);
      
      // Păstrăm conversația curentă selectată dacă există
      const currentId = currentConversation;
      // Reținem și starea private a conversației curente
      const isCurrentPrivate = isPrivate;
      console.log(`În fetchConversations, conversația curentă: ${currentId}, private: ${isCurrentPrivate}`);
      
      // Asigurăm-ne că setăm array-ul în formatul corect
      let conversationsArray = Array.isArray(data) ? data : [];
      
      console.log(`Număr total de conversații primite: ${conversationsArray.length}`);
      
      // Adăugăm conversațiile private din cache la lista de conversații
      // Doar dacă conversația curentă este privată
      if (currentId && isCurrentPrivate) {
        // Verificăm dacă conversația curentă este în cache și nu există în lista obținută
        const currentConvExists = conversationsArray.some(conv => conv.id === currentId);
        
        if (!currentConvExists) {
          // Verificăm cache-ul pentru conversații private
          const cachedConv = privateConversationsCache[currentId];
          
          if (cachedConv) {
            console.log(`Am găsit conversația privată ${currentId} în cache, o adăugăm la lista locală`);
            conversationsArray = [cachedConv, ...conversationsArray];
          } else {
            // Dacă nu e în cache, o încărcăm direct
            try {
              console.log(`Încercăm să încărcăm direct conversația privată ${currentId}`);
              const currentConv = await getConversation(token, currentId);
              if (currentConv) {
                console.log(`Am încărcat conversația privată ${currentId}, o adăugăm la lista locală`);
                conversationsArray = [currentConv, ...conversationsArray];
                
                // Și o adăugăm în cache pentru viitor
                setPrivateConversationsCache(prev => ({
                  ...prev,
                  [currentId]: {
                    id: currentId,
                    title: currentConv.title,
                    model_id: currentConv.model_id,
                    private: true,
                    updated_at: currentConv.updated_at,
                    messages: currentConv.messages
                  }
                }));
              }
            } catch (error) {
              console.error(`Nu s-a putut obține conversația privată ${currentId}:`, error);
            }
          }
        }
      }
      
      // Actualizăm lista de conversații
      setConversations(conversationsArray);
      
      // Verificăm dacă conversația curentă mai există în lista actualizată
      if (currentId && conversationsArray.some(conv => conv.id === currentId)) {
        // Conversația curentă există în lista nouă, o păstrăm selectată
        console.log(`Conversația curentă ${currentId} există în lista actualizată, o păstrăm selectată`);
      } else if (conversationsArray.length > 0 && !currentId) {
        // Nu există o conversație selectată, selectăm prima din listă
        console.log(`Nu există o conversație selectată, selectăm prima din listă: ${conversationsArray[0].id}`);
        setCurrentConversation(conversationsArray[0].id);
        loadConversation(conversationsArray[0].id);
      } else if (currentId && isCurrentPrivate) {
        // În cazul unei conversații private care nu apare în lista actualizată,
        // ne asigurăm că rămâne activă prin reîncărcarea ei directă
        console.log(`Conversația privată ${currentId} nu e în lista principală, o reîncărcăm separat`);
        loadConversation(currentId);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Asigurăm-ne că avem cel puțin o listă goală
      setConversations([]);
    }
  };

  // Creăm o nouă conversație
  const createNewConversation = async () => {
    try {
      // Verificăm dacă există deja o conversație nouă și goală
      const emptyConversation = conversations.find(conv => {
        // Verificăm dacă conversația are titlul implicit
        const hasDefaultTitle = !conv.title || conv.title === 'Conversație nouă';
        
        // Încărcăm conversația pentru a verifica mesajele
        if (hasDefaultTitle && conv.id === currentConversation && messages.length === 0) {
          return true;
        }
        
        return false;
      });
      
      if (emptyConversation) {
        // Dacă există deja o conversație goală, o folosim pe cea existentă
        console.log('Există deja o conversație nouă și goală');
        // Asigurăm-ne că modelul selectat este actualizat cu cel din conversația existentă
        if (emptyConversation.model_id) {
          setSelectedModel(emptyConversation.model_id);
          console.log(`Model setat la: ${emptyConversation.model_id} (din conversația existentă)`);
        }
        // Verificăm și starea private
        setIsPrivate(emptyConversation.private || false);
        return emptyConversation.id;
      }
      
      // Nu mai creăm o conversație nouă în backend
      // În schimb, doar resetăm interfața și așteptăm ca utilizatorul să trimită un mesaj
      console.log('Pregătim interfața pentru o conversație nouă');
      
      // Resetăm conversația curentă și mesajele
      setCurrentConversation(null);
      setMessages([]);
      
      // Setăm un model implicit din lista de modele disponibile
      if (availableModels.length > 0) {
        // Verificăm dacă există modelul implicit preferat în lista de modele disponibile
        const defaultModel = availableModels.find(model => model.id === 'gemma3:12b') || availableModels[0];
        console.log(`Setăm modelul implicit pentru conversația nouă: ${defaultModel.id}`);
        setSelectedModel(defaultModel.id);
      } else {
        console.log('Nu sunt modele disponibile pentru a seta un model implicit');
      }
      
      return null; // Returnăm null pentru că nu am creat încă o conversație
    } catch (error) {
      console.error('Error creating new conversation:', error);
      setBackendError('Nu s-a putut crea o conversație nouă. Verificați conexiunea la server.');
      return null;
    }
  };

  // Încarcă o conversație existentă
  const loadConversation = async (conversationId) => {
    try {
      // Verificăm dacă backend-ul este disponibil
      const backendAvailable = await checkBackendStatus();
      if (!backendAvailable) {
        setBackendError(
          `Nu se poate conecta la backend. Asigurați-vă că serverul FastAPI rulează.`
        );
        return;
      }
      
      const conversation = await getConversation(token, conversationId);
      
      // Setăm conversația curentă
      setCurrentConversation(conversationId);
      
      // Încărcăm mesajele conversației
      setMessages(conversation.messages);
      
      // Actualizăm modelul selectat pentru această conversație
      if (conversation.model_id) {
        console.log(`Încărcare conversație: Model setat la ${conversation.model_id} din conversația ${conversationId}`);
        setSelectedModel(conversation.model_id);
      } else {
        console.log(`Conversația ${conversationId} nu are un model_id specificat`);
      }
      
      // Actualizăm starea private
      const isPrivateConv = conversation.private || false;
      setIsPrivate(isPrivateConv);
      console.log(`Conversație ${conversationId} este ${isPrivateConv ? 'privată' : 'publică'}`);
      
      // Dacă este o conversație privată, o adăugăm în cache
      if (isPrivateConv) {
        console.log(`Adăugăm conversația privată ${conversationId} în cache`);
        setPrivateConversationsCache(prev => ({
          ...prev,
          [conversationId]: {
            id: conversationId,
            title: conversation.title,
            model_id: conversation.model_id,
            private: true,
            updated_at: conversation.updated_at,
            messages: conversation.messages
          }
        }));
        
        // Asigurăm că această conversație există în lista locală
        setConversations(prevConversations => {
          // Verificăm dacă conversația există deja în lista locală
          const exists = prevConversations.some(conv => conv.id === conversationId);
          if (exists) {
            return prevConversations;
          }
          
          // Adăugăm conversația la lista locală fără a afecta filtrarea de pe server
          console.log(`Adăugăm temporar conversația privată ${conversationId} în starea locală pentru navigare`);
          return [conversation, ...prevConversations];
        });
      }
    } catch (error) {
      console.error(`Error loading conversation ${conversationId}:`, error);
    }
  };

  // Oprim generarea
  const stopGeneration = () => {
    if (activeEventSource) {
      activeEventSource.close();
      setActiveEventSource(null);
      setIsLoading(false);
    }
  };

  // Ștergem o conversație
  const deleteConversation = async (conversationId) => {
    try {
      await apiDeleteConversation(token, conversationId);
      
      // Eliminăm conversația din lista locală
      setConversations(conversations.filter(conv => conv.id !== conversationId));
      
      // Dacă era conversația curentă, resetăm
      if (currentConversation === conversationId) {
        setCurrentConversation(null);
        setMessages([]);
      }
    } catch (error) {
      console.error(`Error deleting conversation ${conversationId}:`, error);
    }
  };

  // Afișăm/ascundem selectorul de model
  const toggleModelSelector = () => {
    setShowModelSelector(!showModelSelector);
  };

  // Schimbăm modelul selectat
  const changeModel = (modelId) => {
    setSelectedModel(modelId);
    setShowModelSelector(false);
  };

  // Funcție pentru autentificare
  const handleLogin = (newToken) => {
    setToken(newToken);
  };

  // Funcție pentru delogare
  const handleLogout = () => {
    setToken('');
    setUser(null);
    localStorage.removeItem('token');
    setConversations([]);
    setCurrentConversation(null);
    setMessages([]);
  };

  // Trimitem un mesaj nou
  const sendMessage = async (e) => {
    e?.preventDefault();
    
    if (!input.trim() || isLoading) return;
    
    setIsLoading(true);
    const messageText = input.trim(); // Salvăm mesajul înainte de a reseta input-ul
    
    try {
      // Verificăm dacă backend-ul este disponibil
      const backendAvailable = await checkBackendStatus();
      if (!backendAvailable) {
        setBackendError(
          `Nu se poate conecta la backend. Asigurați-vă că serverul FastAPI rulează.`
        );
        setIsLoading(false);
        return;
      }
      
      // Dacă nu există o conversație curentă, creăm una nouă în backend
      let currentConvId = currentConversation;
      let isNewConversation = false;
      
      if (!currentConvId) {
        // Creăm o conversație nouă în backend doar când se trimite primul mesaj
        console.log(`Creăm o conversație nouă ${isPrivate ? 'privată' : 'publică'} cu modelul implicit al backend-ului`);
        console.log(`DEBUG - Valoarea isPrivate înainte de apel: ${isPrivate}, tip: ${typeof isPrivate}`);
        
        const newConversation = await createConversation(token, null, isPrivate);
        
        if (!newConversation || !newConversation.id) {
          console.error("Răspunsul nu conține o conversație validă");
          setIsLoading(false);
          return;
        }
        
        // Verificăm valoarea private în răspuns
        console.log(`DEBUG - Valoarea private în conversația creată: ${newConversation.private}, tip: ${typeof newConversation.private}`);
        
        // Setăm modelul selectat la cel returnat de backend
        if (newConversation.model_id) {
          console.log(`Model setat la: ${newConversation.model_id} (din conversația nou creată)`);
          setSelectedModel(newConversation.model_id);
        }
        
        // Adăugăm conversația nouă la începutul listei existente
        setConversations(prevConversations => {
          // Verificăm dacă conversația există deja
          const exists = prevConversations.some(conv => conv.id === newConversation.id);
          if (exists) {
            console.log(`Conversația ${newConversation.id} există deja în lista locală`);
            return prevConversations;
          }
          
          console.log(`Adăugăm conversația nouă ${newConversation.id} (private=${newConversation.private}) în lista locală`);
          return [newConversation, ...prevConversations];
        });
        
        currentConvId = newConversation.id;
        isNewConversation = true;
        
        // Setăm conversația curentă la cea nou creată
        setCurrentConversation(currentConvId);
        
        // Așteptăm puțin pentru a ne asigura că conversația este creată în backend
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // Adăugăm mesajul utilizatorului în interfață
      const userMessage = { role: 'user', content: messageText };
      
      // Adăugăm un mesaj gol pentru asistent în interfață (va fi actualizat pe măsură ce se generează răspunsul)
      const assistantMessage = { role: 'assistant', content: '' };
      
      // Adăugăm ambele mesaje în același update pentru a evita condițiile de cursă
      setMessages(prevMessages => [...prevMessages, userMessage, assistantMessage]);
      
      // Resetăm input-ul
      setInput('');
      
      // Pentru conversații noi, generăm un titlu temporar până când backend-ul setează unul permanent
      if (isNewConversation) {
        // Generăm un titlu temporar din primele cuvinte ale mesajului
        const words = messageText.split(' ');
        const tempTitle = words.slice(0, 3).join(' ') + (words.length > 3 ? '...' : '');
        
        // Actualizăm lista de conversații cu titlul temporar
        setConversations(prevConversations => {
          return prevConversations.map(conv => 
            conv.id === currentConvId 
              ? {...conv, title: tempTitle} 
              : conv
          );
        });
      }
      
      // Folosim EventSource pentru streaming
      const url = `${API_BASE_URL}/conversations/${currentConvId}/stream?message=${encodeURIComponent(messageText)}&token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(url);
      setActiveEventSource(eventSource);
      
      let fullResponse = '';
      
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Adăugăm token-ul la răspunsul complet
          if (data.token !== undefined) {
            fullResponse += data.token;
            
            // Actualizăm mesajul asistentului în interfață folosind o funcție de callback
            // pentru a ne asigura că avem întotdeauna ultimul state
            setMessages(prevMessages => {
              // Verificăm dacă array-ul de mesaje are cel puțin 2 elemente
              if (prevMessages.length < 2) {
                console.error("Array-ul de mesaje nu conține suficiente elemente");
                return prevMessages;
              }
              
              // Creăm o copie profundă a array-ului de mesaje
              const updatedMessages = [...prevMessages];
              
              // Actualizăm conținutul ultimului mesaj (mesajul asistentului)
              // dar verificăm mai întâi că există
              const lastIndex = updatedMessages.length - 1;
              if (lastIndex >= 0 && updatedMessages[lastIndex]) {
                updatedMessages[lastIndex] = {
                  ...updatedMessages[lastIndex],
                  content: fullResponse
                };
              } else {
                console.warn("Nu s-a putut actualiza ultimul mesaj, se adaugă un mesaj nou");
                updatedMessages.push({
                  role: 'assistant',
                  content: fullResponse
                });
              }
              
              return updatedMessages;
            });
          }
          
          // Verificăm dacă streaming-ul s-a terminat
          if (data.done) {
            eventSource.close();
            setActiveEventSource(null);
            setIsLoading(false);
            
            // Salvăm conversația curentă
            const currentConvId = currentConversation;
            const isCurrentPrivate = isPrivate;
            
            // Diferențiere între conversațiile publice și private
            if (isCurrentPrivate) {
              // Nu facem refresh pentru conversațiile private!
              console.log(`Conversație privată ${currentConvId} - evităm refresh-ul automat`);
              // Actualizăm interfața fără a reîncărca lista de conversații
              // Acest lucru asigură că utilizatorul poate continua conversația privată
            } else {
              // Doar pentru conversațiile publice, reîncărcăm lista
              console.log('Conversație publică - reîncărcăm lista după răspunsul AI');
              setTimeout(() => {
                fetchConversations();
              }, 1000);
            }
          }
        } catch (error) {
          console.error('Error parsing event data:', error);
        }
      };
      
      eventSource.onerror = (error) => {
        console.error('EventSource error:', error);
        eventSource.close();
        setActiveEventSource(null);
        setIsLoading(false);
        
        // Încearcă să reîncarce conversațiile în caz de eroare
        setTimeout(() => {
          fetchConversations();
        }, 1000);
      };
    } catch (error) {
      console.error('Error sending message:', error);
      setIsLoading(false);
      
      // Încearcă să reîncarce conversațiile în caz de eroare
      setTimeout(() => {
        fetchConversations();
      }, 1000);
    }
  };

  // Toggle pentru modul privat
  const togglePrivateMode = () => {
    setIsPrivate(!isPrivate);
    console.log(`Modul privat ${!isPrivate ? 'activat' : 'dezactivat'}`);
  };

  // Componentă pentru afișarea ecranului de eroare backend
  const BackendErrorScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        <svg 
          className="w-16 h-16 text-red-500 mx-auto mb-4" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth="2" 
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          ></path>
        </svg>
        
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
          Acces Restricționat
        </h1>
        
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Această aplicație este disponibilă doar pentru utilizatorii autorizați. Dacă vedeți acest mesaj, înseamnă că nu aveți permisiunea de a accesa. Vă rugăm să contactați administratorul pentru detalii.
        </p>
        
        <div className="space-y-4">

          
          
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors w-full"
          >
            Verifică din Nou
          </button>
        </div>
      </div>
    </div>
  );
  
  // Afișăm ecranul de verificare dacă încă verificăm backend-ul
  if (checkingBackend) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-700 dark:text-gray-300">Se verifică conexiunea la backend...</p>
        </div>
      </div>
    );
  }
  
  // Afișăm ecranul de eroare dacă backend-ul nu este disponibil
  if (!backendAvailable) {
    return <BackendErrorScreen />;
  }

  // Dacă nu există token sau utilizator, afișăm formularul de autentificare
  if (!token || !user) {
    return <AuthPage onLogin={handleLogin} />;
  }

  // Altfel, afișăm interfața de chat
  return (
    <ChatPage
      user={user}
      conversations={conversations}
      currentConversation={currentConversation}
      messages={messages}
      input={input}
      setInput={setInput}
      isLoading={isLoading}
      showModelSelector={showModelSelector}
      availableModels={availableModels}
      selectedModel={selectedModel}
      isPrivate={isPrivate}
      backendError={backendError}
      messagesEndRef={messagesEndRef}
      handleLogout={handleLogout}
      createNewConversation={createNewConversation}
      loadConversation={loadConversation}
      deleteConversation={deleteConversation}
      toggleModelSelector={toggleModelSelector}
      togglePrivateMode={togglePrivateMode}
      changeModel={changeModel}
      sendMessage={sendMessage}
      stopGeneration={stopGeneration}
    />
  );
}

export default App;